import { BCMSRecord, BCMSStateManagerCRUD, DisabledPrompt, Link, StateManager, useBCMSContext, useModel, useParams, useRoute, useStateManager, useWorkflow } from '@bcm-dev/bcms-vite';
import React from "react";

import FormContainer, { FormButtonContainer, FormRow } from "components/shared/containers/FormContainer";
import { UICancelButton, UIPrimaryButton } from "components/ui/UIButton";
import UIShowHideInput from "components/ui/UIShowHideInput";
import Classes, { CardOnFileMerchant, RecurringPaymentMerchant } from "model";
import "./styles.css";

/**
 * @function EnterSecurityTokenView
 * @param {STRING} email - email tied to account
 * @return {React.Component}
 * @exported true
 * @description The main component for EnterSecurityTokenView
 * @example
 * <EnterSecurityTokenView email="abc@email.com" />
 */
export default function EnterSecurityTokenView(props) {
  const globalController = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, globalController);

  const { goStep, nextStep, prevStep } = useWorkflow() ?? {};
  const navigate = useRoute();
  const model = useModel();
  const phone = model.user.phone;
  const email = model.user.email;
  
  const params = useParams();

  const cardID = useParams().cardID ?? "0";
  const routeID = useParams().routeID ?? "viewdigitalcard";
  const selectionID = props.selectionID
    ? props.selectionID
    : params.selectionID ?? "email";

  const data = { ...getDefaultData(cardID, routeID, selectionID, phone, email), ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };

  if(props.data) {
    if(data.cardOnFileMerchant) {
      data.securityToken = data.cardOnFileMerchant.securityToken;
    }
  }

    // Several workflows share this page,
  // so set their paths here.
  const getPath = routeID => {
    var path = "";
    switch(routeID) {
      case "setpin":
        return "setpin/personpin";
      case "balancetransfers":
        return "balancetransfers";
      default:
        return routeID;
    }
  }

  const controller = (state, setState) => ({
    data: state.data,
    ui: state.ui,

    update: (prop, val) => {
      BCMSStateManagerCRUD.init(state)
        .update([ "data", prop ], val)
        .update([ "ui" ], ui)
        .submit(setState);
    },

    updateTextInput: type => val => {
      const crud = BCMSStateManagerCRUD.init(state)
      .update([ "data", type ], val)
      
      if(data.cardOnFileMerchant) {
        crud.update([ "data", "cardOnFileMerchant", type ], val);
      }
      crud.update([ "ui" ], ui)
      .submit(setState);
    },

    next: () => {
      // Check if this view is in a workflow. 
      // If it is, then go to the next step. Else, default to a navigation to a hardcoded page.
      if(nextStep) {
        nextStep({ data, ui });
      } else {
        navigate(`/home/${ getPath(routeID) }/${ data.cardID }`);
      }
    },

    cancel: () => {
      if(goStep) {
        // Reset the data used in the workflow.
        let clearedMerchant = undefined;
        if(data.cardOnFileSelectedTabType === "recurring") {
          clearedMerchant = RecurringPaymentMerchant.getClearedRecurringPaymentMerchant(data.cardOnFileMerchant);
        } else {
          clearedMerchant = CardOnFileMerchant.getClearedCardOnFileMerchant(data.cardOnFileMerchant);
        }
        const crud = BCMSStateManagerCRUD.init(state);
        crud
        .update([ "data", "cardOnFileMerchant" ], clearedMerchant)
        .update([ "ui" ], ui)
        .submit(setState);

        goStep(0, { data, ui });
      } else {
        navigate(`/home/${ getPath("recenttransactions") }/${ data.cardID }`)
      }
    }
  });

return (
  <StateManager state={ { data, ui } } controller={ controller }>
      <PageContent/>
  </StateManager>
);
}

export const getDefaultData = (cardID, routeID, selectionID, phone, email) => {
  return {
      cardID,
      securityToken: "",
      routeID,
      selectionID,
      phone,
      email,
      revealPassword: false
  };
}

export const getDefaultUI = () => ({

});

function PageContent(props) {
  const { data, ui, cancel, next, update, updateTextInput } = useStateManager();
  const { goStep, nextStep, prevStep } = useWorkflow() ?? {};
  const navigate = useRoute();
  const email = data.email ?? {};
  const phone = data.phone ?? {};
  const masked = email.slice(0, email.indexOf("@")).slice(1).replace(/[A-Za-z]/g, "*");
  const maskedEmail = `${ email.charAt(0) }${ masked }${ email.substring(email.indexOf("@"), email.length) }`;
  const selection = data.selectionID === "email" ? maskedEmail : phone;
  const onShowHideClick = val => update("revealPassword", !val);

  return (
    <div className="enter-security-token-view">
      <div className="section-container">
        <p className="page-description">Help us protect your account by confirming your identity.</p>
        <p>A temporary Security Token has been sent to { selection }. Once you receive the token, enter it below.</p>
      </div>
      <FormContainer>
        <FormRow className="flex-container flex-vertical">
          {/*<UITextInput placeholder="Security Token*" value={ data.securityToken } onChange={ updateTextInput("securityToken") }/>*/}
          <UIShowHideInput revealPassword={ data.revealPassword } placeholder="Security Token*" value={ data.securityToken } 
            onChange={ updateTextInput("securityToken") } onShowHideClick={ onShowHideClick } />
          <DisabledPrompt>
            <Link className="text-right">
              Having Trouble?
            </Link>
          </DisabledPrompt>
        </FormRow>
        <FormButtonContainer>
          <UIPrimaryButton text="Next" className={ data.securityToken.length === 0 ? "disabled" : "" } onClick={ next } dataPoint="Security_Token_Next" />
          <UICancelButton text="Cancel" onClick={ cancel } />
        </FormButtonContainer>
      </FormContainer>
    </div>
  );
}